const uuidv4 = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

export const PRIMARY = '#40518A';

export const RULES_TABLE_GRID_COLS = {
  xs: '1fr',
  sm: 'minmax(0,1.4fr) minmax(0,1.6fr) 96px 112px 100px 100px 96px',
};

export const RULES_TABLE_MIN_WIDTH = 900;

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isValidEmail = (raw) => EMAIL_PATTERN.test(String(raw || '').trim().toLowerCase());

export const PHONE_PATTERN = /^\+?[0-9]{10,15}$/;

export const normalizePhone = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return '';
  const plus = text.startsWith('+');
  const digits = text.replace(/\D/g, '');
  if (!digits) return '';
  return plus ? `+${digits}` : digits;
};

export const isValidPhone = (raw) => PHONE_PATTERN.test(normalizePhone(raw));

/** Parse stored action.value (comma-separated) or form recipients array. */
export const parseWhatsAppRecipients = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => normalizePhone(v)).filter(Boolean);
  }
  return String(value ?? '')
    .split(/[,;]+/)
    .map((v) => normalizePhone(v))
    .filter(Boolean);
};

export const serializeWhatsAppRecipients = (recipients) =>
  parseWhatsAppRecipients(recipients).join(',');

/** Extract read-only preview text from a CRM WhatsApp template object. */
export const getWhatsAppTemplatePreview = (template) => {
  const components = template?.components || [];
  const lines = [];
  const header = components.find((c) => c.type === 'HEADER' && c.text);
  const body = components.find((c) => c.type === 'BODY');
  const footer = components.find((c) => c.type === 'FOOTER');
  if (header?.text) lines.push(`Header: ${header.text}`);
  if (body?.text) lines.push(body.text);
  if (footer?.text) lines.push(`Footer: ${footer.text}`);
  return lines.join('\n\n').trim();
};

/** Parse stored action.value (comma-separated) or form recipients array. */
export const parseMailRecipients = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim().toLowerCase()).filter(Boolean);
  }
  return String(value ?? '')
    .split(/[,;]+/)
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);
};

export const serializeMailRecipients = (recipients) =>
  parseMailRecipients(recipients).join(',');

export const DEFAULT_TIME_VALUE = '09:00:00 AM';
export const DEFAULT_LAST_CHECKOUT_TIME_VALUE = '06:00:00 PM';
export const VIOLATION_VALUE_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const UNMATCH_VALUE_OPTIONS = VIOLATION_VALUE_OPTIONS;

export const POSSIBLE_MATCH_VALUE_OPTIONS = VIOLATION_VALUE_OPTIONS;

export const isUnmatchYesValue = (value) =>
  ['true', '1', 'yes'].includes(String(value || '').trim().toLowerCase());

export const isPossibleMatchYesValue = (value) =>
  ['true', '1', 'yes'].includes(String(value || '').trim().toLowerCase());

export const ruleHasUnmatchYes = (conditions) =>
  (conditions || []).some(
    (c) => c?.condition_type === 'unmatch' && isUnmatchYesValue(c.value)
  );

export const ruleHasPossibleMatchYes = (conditions) =>
  (conditions || []).some(
    (c) => c?.condition_type === 'possible_match' && isPossibleMatchYesValue(c.value)
  );

export const ruleHasViolationYes = (conditions) =>
  (conditions || []).some(
    (c) => c?.condition_type === 'violation' && isPossibleMatchYesValue(c.value)
  );

export const formatEventLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
};

export const PERSON_SCOPE_CONDITION_TYPES = ['employee', 'vendor', 'visitor', 'intrusion'];

export const CONDITION_TYPE_OPTIONS = [
  { value: 'company', label: 'Company' },
  { value: 'camera', label: 'Camera' },
  { value: 'employee', label: 'Employee' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'visitor', label: 'Visitor' },
  { value: 'intrusion', label: 'Intrusion profile' },
  { value: 'violation', label: 'Violation' },
  { value: 'unmatch', label: 'Unmatch' },
  { value: 'possible_match', label: 'Possible match' },
  { value: 'first_checkin', label: 'First check-in' },
  { value: 'last_checkout_time', label: 'Last check-out time' },
  { value: 'time', label: 'Time' },
];

/** Company selected with "Equal to" in a condition before `beforeIndex`. */
export const getPriorCompanyEqualTo = (conditions, beforeIndex) => {
  if (!Array.isArray(conditions) || beforeIndex <= 0) return null;
  for (let i = beforeIndex - 1; i >= 0; i -= 1) {
    const c = conditions[i];
    if (
      c?.condition_type === 'company' &&
      c?.operator === 'equal_to' &&
      String(c?.value || '').trim()
    ) {
      return String(c.value).trim();
    }
  }
  return null;
};

export const canUseCameraCondition = (event) => Boolean(String(event || '').trim());

/** Cameras assigned to the rule event (camera_type on /camera-streams). */
export const getCamerasForEvent = (streams, event) => {
  const eventType = String(event || '').trim().toUpperCase();
  if (!eventType) return [];
  return (streams || [])
    .filter((cam) => {
      const type = String(cam?.camera_type || '').trim().toUpperCase();
      return type === eventType && String(cam?.id || '').trim();
    })
    .map((cam) => ({
      id: String(cam.id).trim(),
      name: String(cam.label || cam.name || cam.id).trim() || String(cam.id),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const canUseEmployeeCondition = (conditions, index) =>
  Boolean(getPriorCompanyEqualTo(conditions, index));

/** Index of nearest employee / vendor / visitor row above `beforeIndex`, or -1. */
export const getPriorPersonScopeIndex = (conditions, beforeIndex) => {
  if (!Array.isArray(conditions) || beforeIndex <= 0) return -1;
  for (let i = beforeIndex - 1; i >= 0; i -= 1) {
    if (PERSON_SCOPE_CONDITION_TYPES.includes(conditions[i]?.condition_type)) {
      return i;
    }
  }
  return -1;
};

/** First check-in row only after a person scope row and when rule event is IN. */
export const canUseFirstCheckinCondition = (conditions, index, event) => {
  if (String(event || '').trim().toUpperCase() !== 'IN') return false;
  return getPriorPersonScopeIndex(conditions, index) >= 0;
};

/** Last check-out time row only after a person scope row and when rule event is OUT. */
export const canUseLastCheckoutTimeCondition = (conditions, index, event) => {
  if (String(event || '').trim().toUpperCase() !== 'OUT') return false;
  return getPriorPersonScopeIndex(conditions, index) >= 0;
};

export const canUseViolationCondition = (event) =>
  String(event || '').trim().toUpperCase() === 'ACTIVITY';

export const canUseUnmatchCondition = (event) => {
  const ev = String(event || '').trim().toUpperCase();
  return ev === 'IN' || ev === 'OUT';
};

export const canUsePossibleMatchCondition = (event) => {
  const ev = String(event || '').trim().toUpperCase();
  return ev === 'IN' || ev === 'OUT' || ev === 'ACTIVITY';
};

export const DEFAULT_POSSIBLE_MATCH_MAIL_SUBJECT = 'Possible match detected';

export const DEFAULT_POSSIBLE_MATCH_MAIL_BODY = `Hello,

Possible match detected at {time}
Top matches
1- {candidate_1} - {matching_score_1}
2- {candidate_2} - {matching_score_2}
3- {candidate_3} - {matching_score_3}

Powered by Kivo.ai`;

/** Bodies replaced automatically when Possible match = Yes is on the rule. */
export const POSSIBLE_MATCH_AUTO_MAIL_BODIES = [
  'A possible match was detected at {camera} on {time}.\n\nBest guess: {best_match} (score {best_score})\n\nTop candidates:\n{candidates}',
  DEFAULT_POSSIBLE_MATCH_MAIL_BODY,
];

export const filterConditionTypeOptions = (typeOptions, conditions, index, event) =>
  typeOptions.filter((opt) => {
    if (opt.value === 'camera') return canUseCameraCondition(event);
    if (opt.value === 'employee') return canUseEmployeeCondition(conditions, index);
    if (opt.value === 'violation') return canUseViolationCondition(event);
    if (opt.value === 'unmatch') return canUseUnmatchCondition(event);
    if (opt.value === 'possible_match') return canUsePossibleMatchCondition(event);
    if (
      ruleHasUnmatchYes(conditions)
      && (
        PERSON_SCOPE_CONDITION_TYPES.includes(opt.value)
        || opt.value === 'first_checkin'
        || opt.value === 'last_checkout_time'
      )
    ) {
      return false;
    }
    if (opt.value === 'first_checkin') return canUseFirstCheckinCondition(conditions, index, event);
    if (opt.value === 'last_checkout_time') return canUseLastCheckoutTimeCondition(conditions, index, event);
    return true;
  });

export const getConditionTypeOptionsForRow = (conditions, index, typeOptions = CONDITION_TYPE_OPTIONS, event) =>
  filterConditionTypeOptions(typeOptions, conditions, index, event);

export const OPERATOR_OPTIONS_BY_TYPE = {
  company: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  camera: [
    { value: 'all', label: 'All' },
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  employee: [
    { value: 'all', label: 'All' },
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  vendor: [
    { value: 'all', label: 'All' },
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  visitor: [
    { value: 'all', label: 'All' },
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  intrusion: [
    { value: 'all', label: 'All' },
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  violation: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  unmatch: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  possible_match: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  first_checkin: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'not_equal_to', label: 'Not Equal to' },
  ],
  last_checkout_time: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'greater_than_equal_to', label: 'Greater than equal to' },
    { value: 'less_than', label: 'Less than' },
    { value: 'less_than_equal_to', label: 'Less than equal to' },
  ],
  time: [
    { value: 'equal_to', label: 'Equal to' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'greater_than_equal_to', label: 'Greater than equal to' },
    { value: 'less_than', label: 'Less than' },
    { value: 'less_than_equal_to', label: 'Less than equal to' },
  ],
};

export const LOGICAL_OPERATORS = ['AND', 'OR'];

export const ACTION_TYPE_OPTIONS = [
  { value: 'send_mail', label: 'Send Mail' },
  { value: 'send_whatsapp', label: 'Send WhatsApp' },
  { value: 'play_speaker', label: 'Play Speaker' },
  { value: 'hooter_alert', label: 'Hooter alert' },
];

export const DEFAULT_MAIL_SUBJECT = 'Alert: {employee}';

export const DEFAULT_MAIL_BODY =
  'Hello,\n\n{employee} was detected at {time}.\n\nPowered by Kivo.ai';

const isPossibleMatchMailTemplate = (action) => {
  if (action?.action_type !== 'send_mail') return false;
  const subject = String(action.mail_subject ?? '').trim();
  const body = String(action.mail_body ?? '').trim();
  return (
    subject === DEFAULT_POSSIBLE_MATCH_MAIL_SUBJECT
    || subject === 'Possible match at {camera}'
    || POSSIBLE_MATCH_AUTO_MAIL_BODIES.some((tpl) => tpl.trim() === body)
  );
};

/** Restore default mail template when possible-match template is no longer applicable. */
export const revertPossibleMatchMailDefaults = (form) => {
  const nextActions = (form.actions || []).map((action) => {
    if (action.action_type !== 'send_mail' || !isPossibleMatchMailTemplate(action)) {
      return action;
    }
    return {
      ...action,
      mail_subject: DEFAULT_MAIL_SUBJECT,
      mail_body: DEFAULT_MAIL_BODY,
    };
  });
  return { ...form, actions: nextActions };
};

/** Apply possible-match mail template when the rule includes Possible match = Yes. */
export const applyPossibleMatchMailDefaults = (form, { force = false } = {}) => {
  if (!ruleHasPossibleMatchYes(form?.conditions)) return form;
  const nextActions = (form.actions || []).map((action) => {
    if (action.action_type !== 'send_mail') return action;
    const subject = String(action.mail_subject ?? '').trim();
    const body = String(action.mail_body ?? '').trim();
    const isGenericDefault =
      (!subject && !body)
      || (subject === DEFAULT_MAIL_SUBJECT && body === DEFAULT_MAIL_BODY)
      || (!body && subject === DEFAULT_MAIL_SUBJECT)
      || (!subject && body === DEFAULT_MAIL_BODY)
      || isPossibleMatchMailTemplate(action);
    if (!force && !isGenericDefault && subject && body) {
      return action;
    }
    return {
      ...action,
      mail_subject: DEFAULT_POSSIBLE_MATCH_MAIL_SUBJECT,
      mail_body: DEFAULT_POSSIBLE_MATCH_MAIL_BODY,
    };
  });
  return { ...form, actions: nextActions };
};

/** Keep send_mail template aligned with Possible match = Yes on/off. */
export const syncMailTemplateForConditions = (form, { force = false } = {}) => {
  if (ruleHasPossibleMatchYes(form?.conditions)) {
    return applyPossibleMatchMailDefaults(form, { force });
  }
  return revertPossibleMatchMailDefaults(form);
};

/** CRM template body markers for auto-selecting WhatsApp templates. */
export const POSSIBLE_MATCH_WHATSAPP_MARKERS = ['possible match', 'top matches'];
export const ATTENDANCE_WHATSAPP_MARKERS = ['attendance alert'];

const whatsAppTemplateSearchText = (template) => {
  const preview = getWhatsAppTemplatePreview(template);
  return `${template?.name || ''}\n${preview}`.toLowerCase();
};

/** Template name suggests possible-match (underscore or spaced). */
export const isPossibleMatchTemplateName = (name) => {
  const n = String(name || '').toLowerCase();
  return n.includes('possible_match') || n.includes('possible match');
};

export const templateMatchesWhatsAppMarkers = (template, markers) => {
  const text = whatsAppTemplateSearchText(template);
  return (markers || []).every((marker) => text.includes(String(marker).toLowerCase()));
};

export const findWhatsAppTemplateByMarkers = (templates, markers) =>
  (templates || []).find((tpl) => templateMatchesWhatsAppMarkers(tpl, markers));

/** Prefer CRM templates named *possible_match*; fall back to body markers. */
export const findPossibleMatchWhatsAppTemplate = (templates) => {
  const list = templates || [];
  const byName = list.find((tpl) => isPossibleMatchTemplateName(tpl?.name));
  if (byName) return byName;
  return findWhatsAppTemplateByMarkers(list, POSSIBLE_MATCH_WHATSAPP_MARKERS);
};

/** Attendance template: kivo_eye_alert name or body markers, excluding possible-match. */
export const findAttendanceWhatsAppTemplate = (templates) => {
  const list = templates || [];
  const byKivoName = list.find((tpl) =>
    String(tpl?.name || '').toLowerCase().includes('kivo_eye_alert')
    && !isPossibleMatchTemplateName(tpl?.name)
  );
  if (byKivoName) return byKivoName;
  return list.find(
    (tpl) =>
      !isPossibleMatchTemplateName(tpl?.name)
      && templateMatchesWhatsAppMarkers(tpl, ATTENDANCE_WHATSAPP_MARKERS)
  );
};

export const whatsAppTemplateForAction = (action, templates) =>
  (templates || []).find(
    (tpl) => String(tpl?.db_id ?? '') === String(action?.whatsapp_template_db_id ?? '')
  );

export const isPossibleMatchWhatsAppTemplate = (template) => {
  if (!template) return false;
  if (isPossibleMatchTemplateName(template?.name)) return true;
  return templateMatchesWhatsAppMarkers(template, POSSIBLE_MATCH_WHATSAPP_MARKERS);
};

export const isAttendanceWhatsAppTemplate = (template) => {
  if (!template) return false;
  if (isPossibleMatchTemplateName(template?.name)) return false;
  return templateMatchesWhatsAppMarkers(template, ATTENDANCE_WHATSAPP_MARKERS);
};

/** True when the user explicitly picked a template in the drawer (not auto-default). */
export const isWhatsAppTemplateUserSelected = (action) =>
  Boolean(action?.whatsapp_template_user_selected);

const shouldAutoApplyWhatsAppTemplate = (action, { force = false } = {}) => {
  if (force) return true;
  if (isWhatsAppTemplateUserSelected(action)) return false;
  return !action.whatsapp_template_db_id;
};

export const actionUsesPossibleMatchWhatsAppTemplate = (action, templates) => {
  const tpl = whatsAppTemplateForAction(action, templates);
  return tpl ? isPossibleMatchWhatsAppTemplate(tpl) : false;
};

export const applyWhatsAppTemplateSelection = (action, template, { userSelected = false } = {}) => ({
  ...action,
  whatsapp_template_id: String(template?.id || ''),
  whatsapp_template_name: String(template?.name || ''),
  whatsapp_template_db_id: template?.db_id ?? null,
  whatsapp_template_user_selected: userSelected,
});

/** Apply possible-match WhatsApp template when the rule includes Possible match = Yes. */
export const applyPossibleMatchWhatsAppDefaults = (form, whatsappTemplates, { force = false } = {}) => {
  if (!ruleHasPossibleMatchYes(form?.conditions)) return form;
  const tpl = findPossibleMatchWhatsAppTemplate(whatsappTemplates);
  if (!tpl) return form;
  const nextActions = (form.actions || []).map((action) => {
    if (action.action_type !== 'send_whatsapp') return action;
    if (!shouldAutoApplyWhatsAppTemplate(action, { force })) return action;
    return applyWhatsAppTemplateSelection(action, tpl);
  });
  return { ...form, actions: nextActions };
};

/** Restore attendance WhatsApp template when possible-match condition is removed. */
export const revertPossibleMatchWhatsAppDefaults = (form, whatsappTemplates) => {
  const autoPossibleTpl = findPossibleMatchWhatsAppTemplate(whatsappTemplates);
  const attendanceTpl = findAttendanceWhatsAppTemplate(whatsappTemplates);
  if (!attendanceTpl) return form;
  const nextActions = (form.actions || []).map((action) => {
    if (action.action_type !== 'send_whatsapp') return action;
    if (isWhatsAppTemplateUserSelected(action)) return action;
    const selected = whatsAppTemplateForAction(action, whatsappTemplates);
    if (!selected || !isPossibleMatchWhatsAppTemplate(selected)) return action;
    if (
      autoPossibleTpl
      && String(selected.db_id ?? '') !== String(autoPossibleTpl.db_id ?? '')
    ) {
      return action;
    }
    return applyWhatsAppTemplateSelection(action, attendanceTpl);
  });
  return { ...form, actions: nextActions };
};

/** Apply attendance WhatsApp template when the rule includes Violation = Yes. */
export const applyViolationWhatsAppDefaults = (form, whatsappTemplates, { force = false } = {}) => {
  if (!ruleHasViolationYes(form?.conditions)) return form;
  const tpl = findAttendanceWhatsAppTemplate(whatsappTemplates);
  if (!tpl) return form;
  const nextActions = (form.actions || []).map((action) => {
    if (action.action_type !== 'send_whatsapp') return action;
    const selected = whatsAppTemplateForAction(action, whatsappTemplates);
    if (
      !force
      && isWhatsAppTemplateUserSelected(action)
      && selected
      && !isPossibleMatchWhatsAppTemplate(selected)
    ) {
      return action;
    }
    if (
      !force
      && action.whatsapp_template_db_id
      && selected
      && !isPossibleMatchWhatsAppTemplate(selected)
    ) {
      return action;
    }
    if (!force && !shouldAutoApplyWhatsAppTemplate(action) && !isPossibleMatchWhatsAppTemplate(selected)) {
      return action;
    }
    return applyWhatsAppTemplateSelection(action, tpl);
  });
  return { ...form, actions: nextActions };
};

/** Keep send_whatsapp template aligned with Possible match = Yes on/off. */
export const syncWhatsAppTemplateForConditions = (form, whatsappTemplates, { force = false } = {}) => {
  if (!Array.isArray(whatsappTemplates) || whatsappTemplates.length === 0) return form;
  if (ruleHasPossibleMatchYes(form?.conditions)) {
    return applyPossibleMatchWhatsAppDefaults(form, whatsappTemplates, { force });
  }
  let next = revertPossibleMatchWhatsAppDefaults(form, whatsappTemplates);
  if (ruleHasViolationYes(form?.conditions)) {
    next = applyViolationWhatsAppDefaults(next, whatsappTemplates, { force });
  }
  return next;
};

/** Sync mail + WhatsApp templates when rule conditions change. */
export const syncActionTemplatesForConditions = (form, whatsappTemplates, { force = false } = {}) => {
  let next = syncMailTemplateForConditions(form, { force });
  next = syncWhatsAppTemplateForConditions(next, whatsappTemplates, { force });
  return next;
};

/** Placeholders allowed only when the matching person-scope condition exists on the rule. */
const SCOPED_MAIL_PLACEHOLDERS = [
  { token: '{vendor}', conditionType: 'vendor', label: 'Vendor' },
  { token: '{visitor}', conditionType: 'visitor', label: 'Visitor' },
  { token: '{intrusion}', conditionType: 'intrusion', label: 'Intrusion profile' },
];

export const ruleConditionTypes = (conditions) => {
  const types = new Set();
  (conditions || []).forEach((cond) => {
    const t = String(cond?.condition_type || '').trim();
    if (t) types.add(t);
  });
  return types;
};

export const getMailTemplatePlaceholderError = (form) => {
  const types = ruleConditionTypes(form?.conditions);
  for (const action of form?.actions || []) {
    if (action.action_type !== 'send_mail') continue;
    const text = `${action.mail_subject || ''}\n${action.mail_body || ''}`;
    for (const { token, conditionType, label } of SCOPED_MAIL_PLACEHOLDERS) {
      if (text.includes(token) && !types.has(conditionType)) {
        return `${token} requires a ${label} condition in this rule`;
      }
    }
  }
  return null;
};

const TIME_OPERATORS = OPERATOR_OPTIONS_BY_TYPE.time.map((o) => o.value);

export const normalizeConditionForForm = (cond) => {
  const base = { id: uuidv4(), ...cond };
  if (base.condition_type === 'first_checkin_time') {
    return {
      ...base,
      condition_type: 'first_checkin',
      operator: 'equal_to',
      value: 'true',
    };
  }
  if (base.condition_type === 'first_checkin' && !base.value) {
    return { ...base, value: 'true' };
  }
  if (base.condition_type === 'last_checkout_time' && !base.value) {
    return { ...base, value: DEFAULT_LAST_CHECKOUT_TIME_VALUE };
  }
  if (base.condition_type === 'violation' && !base.value) {
    return { ...base, value: 'true' };
  }
  if (base.condition_type === 'unmatch' && !base.value) {
    return { ...base, value: 'true' };
  }
  if (base.condition_type === 'possible_match' && !base.value) {
    return { ...base, value: 'true' };
  }
  return base;
};

export const emptyCondition = () => ({
  id: uuidv4(),
  condition_type: 'company',
  operator: 'equal_to',
  value: '',
  logical_operator: 'AND',
});

export const emptyAction = () => ({
  id: uuidv4(),
  action_type: '',
  value: '',
  speaker_id: '',
  mail_subject: '',
  mail_body: '',
  whatsapp_template_id: '',
  whatsapp_template_name: '',
  whatsapp_template_db_id: null,
});

/** Defaults applied when the user picks Play Speaker in the action dropdown. */
export const applyPlaySpeakerActionDefaults = (action = {}) => ({
  ...action,
  action_type: 'play_speaker',
  value: String(action.value ?? '').trim(),
  speaker_id: String(action.speaker_id ?? '').trim(),
  mail_subject: '',
  mail_body: '',
  recipients: [],
  whatsapp_template_id: '',
  whatsapp_template_name: '',
  whatsapp_template_db_id: null,
});

/** Defaults applied when the user picks Hooter alert (uses Hooter Configuration page). */
export const applyHooterAlertActionDefaults = (action = {}) => ({
  ...action,
  action_type: 'hooter_alert',
  value: '',
  speaker_id: '',
  mail_subject: '',
  mail_body: '',
  recipients: [],
  whatsapp_template_id: '',
  whatsapp_template_name: '',
  whatsapp_template_db_id: null,
});

/** Defaults applied when the user picks Send Mail in the action dropdown. */
export const applySendMailActionDefaults = (action = {}) => ({
  ...action,
  action_type: 'send_mail',
  recipients: Array.isArray(action.recipients) && action.recipients.length
    ? action.recipients
    : parseMailRecipients(action.value),
  mail_subject: String(action.mail_subject ?? '').trim() || DEFAULT_MAIL_SUBJECT,
  mail_body: String(action.mail_body ?? '').trim() || DEFAULT_MAIL_BODY,
});

/** Defaults applied when the user picks Send WhatsApp in the action dropdown. */
export const applySendWhatsAppActionDefaults = (action = {}) => ({
  ...action,
  action_type: 'send_whatsapp',
  recipients: Array.isArray(action.recipients) && action.recipients.length
    ? action.recipients
    : parseWhatsAppRecipients(action.value),
  whatsapp_template_id: String(action.whatsapp_template_id ?? '').trim(),
  whatsapp_template_name: String(action.whatsapp_template_name ?? '').trim(),
  whatsapp_template_db_id: action.whatsapp_template_db_id ?? null,
});

/** Ensure send_mail actions have template fields when loading from API. */
export const normalizeActionForForm = (action) => {
  const base = { id: uuidv4(), ...action };
  if (base.action_type === 'send_mail') {
    return {
      ...base,
      recipients: parseMailRecipients(base.recipients ?? base.value),
      mail_subject: String(base.mail_subject ?? '').trim() || DEFAULT_MAIL_SUBJECT,
      mail_body: String(base.mail_body ?? '').trim() || DEFAULT_MAIL_BODY,
    };
  }
  if (base.action_type === 'send_whatsapp') {
    return {
      ...base,
      recipients: parseWhatsAppRecipients(base.recipients ?? base.value),
      whatsapp_template_id: String(base.whatsapp_template_id ?? '').trim(),
      whatsapp_template_name: String(base.whatsapp_template_name ?? '').trim(),
      whatsapp_template_db_id: base.whatsapp_template_db_id ?? null,
      whatsapp_template_user_selected: Boolean(base.whatsapp_template_db_id),
    };
  }
  if (base.action_type === 'play_speaker') {
    return {
      ...base,
      value: String(base.value ?? '').trim(),
      speaker_id: String(base.speaker_id ?? '').trim(),
    };
  }
  if (base.action_type === 'hooter_alert') {
    return { ...base, value: '' };
  }
  return base;
};

export const emptyRuleForm = () => ({
  name: '',
  description: '',
  event: '',
  conditions: [emptyCondition()],
  actions: [emptyAction()],
  is_active: true,
});

export const DUPLICATE_RULE_MESSAGE = 'Rule already exist';

const normalizeConditionForSignature = (cond, index, total) => {
  const out = {
    condition_type: String(cond?.condition_type || '').trim().toLowerCase(),
    operator: String(cond?.operator || '').trim().toLowerCase(),
    value: String(cond?.value || '').trim(),
  };
  if (index < total - 1) {
    out.logical_operator = String(cond?.logical_operator || 'AND').trim().toUpperCase();
  }
  return out;
};

/** Match backend json.dumps(..., sort_keys=True, separators=(',', ':')) */
const stableStringify = (value) => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
};

/** Canonical event + conditions payload for duplicate detection (actions excluded). */
export const ruleSignaturePayload = (rule) => {
  const conditions = rule?.conditions || [];
  return {
    event: String(rule?.event || '').trim().toUpperCase(),
    conditions: conditions.map((cond, index) =>
      normalizeConditionForSignature(cond, index, conditions.length)
    ),
  };
};

export const ruleSignature = (rule) => stableStringify(ruleSignaturePayload(rule));

export const findDuplicateRule = (candidate, existingRules = [], excludeRuleId = null) => {
  const signature = ruleSignature(candidate);
  return (
    (existingRules || []).find((rule) => {
      if (excludeRuleId && rule?.id === excludeRuleId) return false;
      return ruleSignature(rule) === signature;
    }) || null
  );
};

export const formToRule = (form) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  event: form.event,
  conditions: (form.conditions || []).map(({ id: _id, ...rest }) => rest),
  actions: (form.actions || []).map(({ id: _id, recipients, ...rest }) => {
    if (rest.action_type === 'send_mail') {
      const list = Array.isArray(recipients) ? recipients : parseMailRecipients(rest.value);
      return { ...rest, value: serializeMailRecipients(list) };
    }
    if (rest.action_type === 'send_whatsapp') {
      const list = Array.isArray(recipients) ? recipients : parseWhatsAppRecipients(rest.value);
      const { whatsapp_template_user_selected: _userSelected, ...whatsappRest } = rest;
      return { ...whatsappRest, value: serializeWhatsAppRecipients(list) };
    }
    return rest;
  }),
  is_active: form.is_active !== false,
});

export const ruleToForm = (rule) =>
  syncMailTemplateForConditions({
    name: rule.name || '',
    description: rule.description || '',
    event: rule.event || '',
    conditions: (rule.conditions || []).map((c) => normalizeConditionForForm(c)),
    actions: (rule.actions || []).map((a) => normalizeActionForForm(a)),
    is_active: rule.is_active !== false,
  });

export const isFormValid = (form) => {
  if (!form.name.trim()) return false;
  if (!form.event) return false;
  if (!Array.isArray(form.conditions) || form.conditions.length === 0) return false;
  for (const cond of form.conditions) {
    if (!cond.condition_type || !cond.operator) return false;
    if (cond.condition_type === 'last_checkout_time' && !cond.value) {
      return false;
    }
    if (cond.condition_type === 'first_checkin' && !cond.value) return false;
    if (cond.operator !== 'all' && !cond.value) return false;
  }
  if (!Array.isArray(form.actions) || form.actions.length === 0) return false;
  for (const action of form.actions) {
    if (!action.action_type) return false;
    if (action.action_type === 'send_mail') {
      const emails = parseMailRecipients(action.recipients ?? action.value);
      if (emails.length === 0 || !emails.every((e) => isValidEmail(e))) return false;
      if (!String(action.mail_subject || '').trim()) return false;
      if (!String(action.mail_body || '').trim()) return false;
      continue;
    }
    if (action.action_type === 'send_whatsapp') {
      const phones = parseWhatsAppRecipients(action.recipients ?? action.value);
      if (phones.length === 0 || !phones.every((p) => isValidPhone(p))) return false;
      if (!action.whatsapp_template_db_id) return false;
      continue;
    }
    if (action.action_type === 'play_speaker') {
      if (!String(action.value || '').trim()) return false;
      if (!String(action.speaker_id || '').trim()) return false;
      continue;
    }
    if (action.action_type === 'hooter_alert') {
      continue;
    }
    if (!action.value.trim()) return false;
  }
  if (getMailTemplatePlaceholderError(form)) return false;
  return true;
};
